/**
 * QuantityButton Component
 *
 * This component provides a user interface for adjusting quantity values within a specified range.
 * It includes increment and decrement buttons, as well as an input field for direct quantity entry.
 *
 * Props:
 * @param {number} inStock - The maximum quantity available (default: 1)
 * @param {number} quantity - The initial quantity value (default: 1)
 * @param {function} onQuantityChange - Callback function triggered when quantity changes
 *
 * Features:
 * - Increment and decrement buttons for easy quantity adjustment
 * - Input field for direct quantity entry
 * - Automatic validation to ensure quantity stays within valid range (1 to inStock)
 * - Disables increment button when maximum quantity (inStock) is reached
 * - Updates parent component via onQuantityChange callback
 *
 * Usage:
 * ```jsx
 * import QuantityButton from "@/components/ui/quantity-button"
 *
 * <QuantityButton
 *   inStock={10}
 *   quantity={1}
 *   onQuantityChange={(newQuantity) => console.log(newQuantity)}
 * />
 * ```
 *
 * The component uses React hooks (useState, useEffect) for state management
 * and includes custom logic for handling user interactions and input validation.
 */

"use client";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Minus, Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { setCartInputValue } from "@/app/redux/cart-management/cart-management-slice";

const QuantityButton = ({ product, inStock = 1, quantity = 1, onQuantityChange = quantity => {} }) => {
  const [count, setCount] = useState(quantity);
  const [inputValue, setInputValue] = useState(quantity.toString());
  const dispatch = useDispatch();
  const cartInputValue = useSelector(state => state?.cart?.cartInputValue);
  useEffect(() => {
    setCount(quantity);
    setInputValue(quantity.toString());
  }, [quantity]);

  const maxQuantityReached = inStock === count;

  useEffect(() => {
    setInputValue(count.toString());
  }, [count]);

  const updateCartQtyState = newValue => {
    let copyOfCartInput = [...cartInputValue];
    let cartItemIndex = cartInputValue?.findIndex(item => item.id === product?.id);
    let cartUpdatedQty = {
      qty: newValue,
      id: copyOfCartInput[cartItemIndex]?.id || product?.data?.id,
      input: true,
    };
    copyOfCartInput.splice(cartItemIndex, 1);
    copyOfCartInput.splice(cartItemIndex, 0, cartUpdatedQty);
    dispatch(setCartInputValue(copyOfCartInput));
  };

  const updateQuantity = newValue => {
    const validValue = Math.max(1, Math.min(newValue, inStock));
    updateCartQtyState(validValue);
    onQuantityChange(validValue);
  };

  const incrementQuantity = () => updateQuantity(count + 1);
  const decrementQuantity = () => updateQuantity(count - 1);

  const handleInputChange = e => {
    const value = e.target.value;
    // Check if the value is a whole number (integer) and within the inStock limit
    // Prevents any input with decimal points
    if (/^\d*$/.test(value) && Number(value) <= inStock) {
      updateCartQtyState(value);
      setInputValue(value);
    }
  };

  const handleInputBlur = () => {
    const newValue = parseInt(inputValue, 10);
    if (!isNaN(newValue) && newValue >= 1) {
      updateQuantity(newValue);
      updateCartQtyState(newValue);
      // dispatch(setCartInputValue(newValue))
    } else {
      setInputValue(count.toString());
    }
  };
  return (
    <div className="flex w-full h-full items-center border border-system-grey rounded hover:border-midnightIndigo-400 overflow-hidden transition-all duration-300 p-2 ">
      <Minus
        role="button"
        onClick={decrementQuantity}
        disabled={count <= 1}
        className={`h-[25px] w-[25px] text-midnightIndigo-400  ${count <= 1 ? "opacity-50" : ""}`}
      />

      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        className="border-0 ring-0  md:h-[36px] lg:h-full   !p-0 !pt-1   text-center focus:outline-none focus-visible:ring-0 focus:border-system-none  text-[16px] hover:!ring-0"
      />
      <Plus
        role="button"
        disabled={maxQuantityReached}
        onClick={incrementQuantity}
        className={`h-[25px] w-[25px] text-midnightIndigo-400  ${maxQuantityReached ? "opacity-50" : ""}`}
      />
    </div>
  );
};

export default QuantityButton;
